import { Popup } from '@extranet-ts/components/common/popup';
import { NOTIFICATION_CONTEXT, NotificationContext } from '@extranet-ts/components/contexts/notification_context';
import CheckCircle from '@extranet-ts/components/icons/check_circle';
import FiberManual from '@extranet-ts/components/icons/fiber_manual';
import { useResize } from '@extranet-ts/hooks/use_resize';
import React, { useContext, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { USER_CONTEXT, UserContext } from './contexts/user_context';

type ChecklistItem = {
  label: string;
  text: string;
  link: string;
  add_text?: string;
  done: () => boolean;
  percent: number;
};

type Props = {
  showIndicator?: boolean;
  small?: boolean;
  onGoTo?: (event: React.MouseEvent) => void;
  camp?: CampType;
};

type CampType = {
  apartments_count: number;
  has_prices: boolean;
  tariffs_with_apartment_count: number;
  pictures_count: number;
  camp_contacts_for_guests: boolean;
  camp_contacts_for_booking: boolean;
  service_tags_count: number;
  settings_filled: boolean;
  on_moderate: boolean;
};

export const ModerationCamp = ({ showIndicator = true, small = false, onGoTo, camp }: Props) => {
  const { updateCurrentCamp, current_camp } = useContext<USER_CONTEXT>(UserContext);

  const currentCamp = camp || current_camp;

  const {
    apartments_count,
    has_prices,
    tariffs_with_apartment_count,
    pictures_count,
    camp_contacts_for_guests,
    camp_contacts_for_booking,
    service_tags_count,
    settings_filled,
    on_moderate,
  } = currentCamp;

  const notyContext = useContext<NOTIFICATION_CONTEXT>(NotificationContext);

  const ref = useRef(null);
  const { isMobile } = useResize();

  const [showPopup, setShowPopup] = useState(false);
  const timeoutRef = useRef(null);

  const handleClickLink = (event: React.MouseEvent) => {
    onGoTo?.(event);
  };

  const checklist: ChecklistItem[] = [
    {
      label: 'Добавить',
      text: 'категории номеров',
      link: '/tariffs/apartments',
      done: () => apartments_count > 0,
      percent: 10,
    },
    {
      label: 'Указать',
      text: 'тарифы',
      link: '/tariffs',
      done: () => tariffs_with_apartment_count > 0,
      percent: 10,
    },
    {
      label: 'Указать',
      text: 'цены',
      link: '/tariffs/prices',
      done: () => has_prices,
      percent: 10,
    },
    {
      label: 'Добавить',
      text: 'фотографии',
      add_text: '- не менее 5',
      link: '/gallery',
      done: () => pictures_count > 4,
      percent: 10,
    },
    {
      label: 'Указать',
      text: 'контакты',
      link: '/contacts',
      add_text: 'для гостей и для бронирований',
      done: () => camp_contacts_for_guests && camp_contacts_for_booking,
      percent: 10,
    },
  ];

  const checklistAdditional: ChecklistItem[] = [
    {
      label: 'Добавить',
      text: 'услуги',
      link: '/camp/services',
      done: () => service_tags_count > 0,
      percent: 17,
    },
    {
      label: 'Добавить',
      text: 'больше 5',
      link: '/gallery',
      add_text: 'фотографий',
      done: () => pictures_count > 5,
      percent: 17,
    },
    {
      label: 'Указать',
      text: 'настройки объекта',
      link: '/camp/settings',
      done: () => settings_filled,
      percent: 16,
    },
  ];

  const allRequiredDone = checklist.every((item) => item.done());

  const getProgress = () => {
    const requiredProgress = checklist.reduce((sum, item) => sum + (item.done() ? item.percent : 0), 0);

    const additionalProgress = checklistAdditional.reduce((sum, item) => sum + (item.done() ? item.percent : 0), 0);

    return requiredProgress + additionalProgress;
  };

  const fullChecks: ChecklistItem[] = [...checklist, ...checklistAdditional];

  const sendToModeration = () => {
    fetch('/api/send_to_moderation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((r) => {
        if (!r.ok) {
          throw new Error();
        }
        updateCurrentCamp((camp) => ({
          ...camp,
          on_moderate: true,
        }));
        setShowPopup(false);
      })
      .catch(() => {
        notyContext.addNotification({ type: 'danger', title: 'Произошла ошибка при отправке на модерацию' });
      });
  };

  const moderationButton = (
    <button onClick={sendToModeration} className={`moderation__complete ${small ? 'moderation__complete_small' : ''}`}>
      Отправить на модерацию
    </button>
  );

  const openPopup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowPopup(true);
  };

  const closePopup = () => {
    timeoutRef.current = setTimeout(() => {
      setShowPopup(false);
    }, 100);
  };

  return (
    <div
      className={`moderation ${!allRequiredDone ? 'moderation__incomplete' : ''}`}
      onMouseEnter={openPopup}
      onMouseLeave={closePopup}
    >
      {!allRequiredDone && !on_moderate && (
        <div className={`moderation__indicator_draft  ${small ? 'moderation__indicator_small' : ''}`}>Черновик</div>
      )}

      <Popup
        anchor={ref.current}
        title={'Перед публикацией'}
        open={showPopup}
        id={'moderation_popup'}
        onClose={() => setShowPopup(false)}
      >
        <div className='moderation__dropdown' onMouseEnter={openPopup} onMouseLeave={closePopup}>
          {fullChecks.map((item, index) => (
            <div key={index} className='moderation__dropdown_item'>
              <div>{item.done() ? <CheckCircle size={16} /> : <FiberManual size={16} />}</div>
              <div className='moderation__dropdown_right'>
                <div>
                  <span className={item.done() ? 'moderation__label_done' : ''}>{item.label}</span>{' '}
                  <NavLink to={item.link} className={'moderation__text'} onClick={handleClickLink}>
                    {item.text}
                  </NavLink>{' '}
                  <span>{item?.add_text}</span>
                </div>
                <span className={item.done() ? 'moderation__percent moderation__percent_done' : 'moderation__percent'}>
                  {item.percent}%
                </span>
              </div>
            </div>
          ))}
          {getProgress() >= 50 && !on_moderate && isMobile && moderationButton}
        </div>
      </Popup>
      {!on_moderate && showIndicator && (
        <div
          ref={ref}
          className={`moderation__indicator_block ${!allRequiredDone ? 'moderation__indicator_block_incomplete' : ''}`}
        >
          {getProgress()}%
          <div className='moderation__indicator' style={{ '--progress': getProgress() } as React.CSSProperties} />
        </div>
      )}
      {allRequiredDone && !on_moderate && moderationButton}
      {on_moderate && (
        <div
          className={`moderation__complete moderation__complete_on-moderate ${small ? 'moderation__complete_small' : ''}`}
        >
          На модерации
        </div>
      )}
    </div>
  );
};
